import { PaymentAttemptStatus } from '../../../__generated__/graphql';

/** The fields of `createTripExtensionPayment` this component acts on. */
export interface ExtensionPaymentOutcome {
  status: PaymentAttemptStatus;
  paymentIntentId?: string | null;
  clientSecret?: string | null;
  amount: number;
  errorMessage?: string | null;
  extensionBlocked: boolean;
}

export type ExtensionPaymentStep =
  | { kind: 'confirm'; paymentIntentId: string; amount: number }
  | { kind: 'authenticate'; clientSecret: string; paymentIntentId: string; amount: number }
  | { kind: 'failed'; message: string; blocked: boolean };

export const EXTENSION_BLOCKED_MESSAGE =
  'Extensions are no longer available for this trip because several payments were declined. Please return the car at the scheduled time.';

const GENERIC_FAILURE = 'The payment did not go through. Please try another card.';

/**
 * Decides what the modal does with the server's answer (spec 013 D6). The server has
 * already charged or tried to charge; this only routes to confirm, 3DS, or an error.
 */
export function nextExtensionPaymentStep(result: ExtensionPaymentOutcome): ExtensionPaymentStep {
  if (result.extensionBlocked && result.status !== PaymentAttemptStatus.Succeeded) {
    return {
      kind: 'failed',
      message: result.errorMessage || EXTENSION_BLOCKED_MESSAGE,
      blocked: true,
    };
  }
  if (result.status === PaymentAttemptStatus.Succeeded && result.paymentIntentId) {
    return { kind: 'confirm', paymentIntentId: result.paymentIntentId, amount: result.amount };
  }
  if (
    result.status === PaymentAttemptStatus.RequiresAction &&
    result.clientSecret &&
    result.paymentIntentId
  ) {
    return {
      kind: 'authenticate',
      clientSecret: result.clientSecret,
      paymentIntentId: result.paymentIntentId,
      amount: result.amount,
    };
  }
  return { kind: 'failed', message: result.errorMessage || GENERIC_FAILURE, blocked: false };
}

/**
 * The words shown inline when `createTripExtensionPayment` itself is refused (the
 * request threw instead of answering FAILED): the server's message, such as
 * `HOST_LISTINGS_PAUSED` ("The host is paused on <Brand>, so this trip can't be
 * extended…", D41), else the error's own text.
 */
export function extensionRequestErrorMessage(error: unknown): string {
  const graphql = (
    error as { graphQLErrors?: Array<{ message?: string }> } | null
  )?.graphQLErrors?.[0]?.message?.trim();
  if (graphql) return graphql;
  const own = error instanceof Error ? error.message.trim() : '';
  return own || 'Payment failed';
}

/** True when the server's price differs from what the guest was shown by at least a cent. */
export function serverPriceDiffers(shownAmount: number, serverAmount: number): boolean {
  return Math.abs(shownAmount - serverAmount) >= 0.01;
}

/** The picker value that opens the new-card form instead of charging a listed card. */
export const NEW_CARD_CHOICE = 'new';

/** The fields of a `bookingPaymentOptions` card the picker shows. */
export interface ExtensionCardOption {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  wallet?: string | null;
  isExpired: boolean;
  usedByThisBooking?: boolean | null;
}

export { cardExpiry, cardTitle } from '../../utils/cardLabels';

/**
 * The card selected when the picker opens: the one the page passed in, else the
 * booking's preferred card, else the first usable card, else the new-card form.
 * An expired card is never preselected.
 */
export function initialCardChoice(
  cards: ExtensionCardOption[],
  preferredPaymentMethodId?: string | null,
  requestedPaymentMethodId?: string | null,
): string {
  const usable = cards.filter(card => !card.isExpired);
  for (const id of [requestedPaymentMethodId, preferredPaymentMethodId]) {
    if (id && usable.some(card => card.id === id)) return id;
  }
  return usable[0]?.id ?? NEW_CARD_CHOICE;
}

/** The card to try after `failedId` was declined: the next usable one, else the new-card form. */
export function nextCardAfterDecline(cards: ExtensionCardOption[], failedId: string): string {
  const usable = cards.filter(card => !card.isExpired && card.id !== failedId);
  return usable[0]?.id ?? NEW_CARD_CHOICE;
}
